import { Injectable, Inject } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { ROUTE_CACHE_TARGETS } from '../core/tokens';

/**
 * Custom RouteReuseStrategy, die die Root-Komponente (z.B. Kartenansicht)
 * beim Navigieren zu konfigurierten Ziel-Routes erhält und bei Rückkehr wiederherstellt.
 *
 * Das vermeidet den kompletten Neuaufbau schwerer Komponenten (z.B. Leaflet-Karte),
 * was besonders auf Tablets die Performance deutlich verbessert.
 *
 * Die Ziel-Routes werden über den ROUTE_CACHE_TARGETS InjectionToken konfiguriert.
 */
@Injectable()
export class WdMapCacheRouteReuseStrategy implements RouteReuseStrategy {
	private storedRoutes = new Map<string, DetachedRouteHandle>();

	/** Merkt sich die Ziel-Route für shouldDetach/store */
	private pendingTarget: string | null = null;

	/** Routes zu denen navigiert wird und bei denen die Root-Route gecacht werden soll */
	private readonly cacheForTargets: Set<string>;

	constructor(@Inject(ROUTE_CACHE_TARGETS) targets: string[]) {
		this.cacheForTargets = new Set(targets);
	}

	private getRouteKey(route: ActivatedRouteSnapshot): string {
		return route.routeConfig?.path ?? '';
	}

	/**
	 * Bestimmt, ob die Route wiederverwendet werden kann.
	 * Wird VOR shouldDetach aufgerufen - hier merken wir uns das Ziel.
	 */
	shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
		const futureKey = this.getRouteKey(future);
		const currKey = this.getRouteKey(curr);

		// Merke das Ziel für shouldDetach/store
		if (currKey === '' && futureKey !== currKey) {
			this.pendingTarget = futureKey;
		} else {
			this.pendingTarget = null;
		}

		return future.routeConfig === curr.routeConfig;
	}

	/**
	 * Bestimmt, ob die Route beim Verlassen gespeichert werden soll.
	 * Nur cachen wenn von Root-Route zu einer konfigurierten Ziel-Route navigiert wird.
	 */
	shouldDetach(route: ActivatedRouteSnapshot): boolean {
		const key = this.getRouteKey(route);
		if (key !== '') return false;

		// Nur cachen wenn Ziel eine Cache-Route ist
		return this.pendingTarget !== null && this.cacheForTargets.has(this.pendingTarget);
	}

	/**
	 * Speichert die Route-Handle für spätere Wiederverwendung.
	 */
	store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
		const key = this.getRouteKey(route);
		if (handle && key === '') {
			this.storedRoutes.set(key, handle);
		} else if (!handle) {
			this.storedRoutes.delete(key);
		}
	}

	/**
	 * Bestimmt, ob eine gespeicherte Route wiederverwendet werden soll.
	 */
	shouldAttach(route: ActivatedRouteSnapshot): boolean {
		const key = this.getRouteKey(route);
		if (key !== '') return false;
		return this.storedRoutes.has(key);
	}

	/**
	 * Gibt die gespeicherte Route-Handle zurück.
	 */
	retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
		const key = this.getRouteKey(route);
		return this.storedRoutes.get(key) ?? null;
	}

	/**
	 * Löscht den Cache für alle oder bestimmte Routes.
	 */
	clearCache(routeKey?: string): void {
		if (routeKey !== undefined) {
			this.storedRoutes.delete(routeKey);
		} else {
			this.storedRoutes.clear();
		}
	}
}
